import { FieldError, FieldGroup, FieldHelper, Label, TextArea, composeTailwindRenderProps } from '@vizit/atoms';
import type { FieldProps } from '@vizit/atoms';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { TextField as AriaTextField, type TextFieldProps as AriaTextFieldProps } from 'react-aria-components';

interface TextAreaFieldProps extends FieldProps, AriaTextFieldProps {
  /**
   * The placeholder of the text area.
   *
   * @optional
   * @type string
   */
  placeholder?: string;
  /**
   * The number of visible text lines.
   *
   * @optional
   * @type number
   * @default 3
   */
  rows?: number;
  /**
   * A ref used to connect a form library to the text area.
   *
   * @optional
   * @type React.Ref<HTMLTextAreaElement>
   */
  textAreaRef?: React.Ref<HTMLTextAreaElement>;
  /**
   * Additional props to pass to the text area element.
   *
   * @optional
   * @type React.TextareaHTMLAttributes<HTMLTextAreaElement>
   */
  textAreaProps?: React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
    /** Test ID for E2E tests */
    'data-testid'?: string;
  };
  /**
   * Additional className to pass to the FieldGroup wrapper.
   *
   * @optional
   * @type string
   */
  groupClassName?: string;
}

/**
 * The TextAreaField component is a multi-line counterpart of TextField: a
 * label, a bordered text area, an optional helper and an error message.
 *
 * @param props - The props for the TextAreaField component.
 * @example
 * <TextAreaField label="Description" placeholder="What is this for?" rows={3} />
 */
function TextAreaField(props: TextAreaFieldProps, ref: React.ForwardedRef<HTMLDivElement>) {
  const {
    className,
    description,
    errorMessage,
    isDisabled,
    isInvalid,
    isRequired,
    label,
    placeholder,
    rows = 3,
    maxLength,
    textAreaRef,
    textAreaProps,
    groupClassName,
    ...other
  } = props;

  const { className: textAreaClassName, ...otherTextAreaProps } = textAreaProps ?? {};

  return (
    <AriaTextField
      {...other}
      ref={ref}
      className={composeTailwindRenderProps(className, 'group flex min-w-[40px] flex-col gap-3')}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      isRequired={isRequired}
    >
      {label && <Label isRequired={isRequired}>{label}</Label>}
      <FieldGroup
        className={cx('h-auto items-stretch py-4 focus-within:py-[7px]', groupClassName)}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
      >
        <TextArea
          ref={textAreaRef}
          className={cx('h-full', textAreaClassName)}
          maxLength={maxLength}
          placeholder={placeholder}
          rows={rows}
          {...otherTextAreaProps}
        />
      </FieldGroup>
      {description && <FieldHelper>{description}</FieldHelper>}
      <FieldError>{errorMessage}</FieldError>
    </AriaTextField>
  );
}

const _TextAreaField = React.forwardRef<HTMLDivElement, TextAreaFieldProps>(TextAreaField);

export type { TextAreaFieldProps };
export { _TextAreaField as TextAreaField };
